import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { stripePromise, SUBSCRIPTION_PLANS } from '../lib/stripe';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
  },
};

const SubscriptionForm = ({ profile, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: profile.full_name,
          email: profile.email,
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Create subscription via API
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await supabase.auth.getSession().then(s => s.data.session?.access_token)}`
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          priceId: SUBSCRIPTION_PLANS.SOLO.id,
          trialDays: 14
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create subscription');
      }

      const { subscription } = await response.json();

      // Update profile with subscription info
      await authService.updateProfile({
        stripe_customer_id: subscription.customer,
        subscription_id: subscription.id,
        subscription_status: subscription.status,
        trial_ends_at: new Date(subscription.trial_end * 1000).toISOString()
      });

      toast.success('Free trial started! Welcome to QuoteTier.');
      
      // Refresh the auth context to update user profile
      window.location.reload();
      onSuccess();
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to start trial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="form-label">Card Information</label>
        <div className="mt-1 p-3 border border-gray-300 rounded-md">
          <CardElement options={cardElementOptions} />
        </div>
        <p className="mt-2 text-xs text-gray-600">
          This is the legacy Pro trial setup path. New users can start on the free plan without a card.
        </p>
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <div className="flex items-center justify-center" role="status" aria-live="polite">
            <div className="spinner spinner-sm mr-2" aria-hidden="true"></div>
            <span>Starting Your Free Trial…</span>
          </div>
        ) : (
          'Start Free — 3 Quotes/Mo'
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        By continuing, you agree to our Terms of Service and Privacy Policy.
        Cancel anytime during your trial period.
      </p>
    </form>
  );
};

const SubscriptionOnboarding = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setProfile(user.profile);
    } catch (error) {
      console.error('Load profile error:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center" role="status" aria-live="polite">
          <div className="spinner spinner-lg mx-auto mb-4" aria-hidden="true"></div>
          <p className="text-gray-700 font-jetbrains">Loading…</p>
          <span className="sr-only">Content is loading</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Legacy Pro Trial Setup</h1>
          <p className="mt-2 text-gray-600">
            This protected route is retained for old subscription flows. The current default path is the free plan.
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="text-center mb-6">
            <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mt-3 text-lg font-semibold text-gray-900">
              Solo Plan - 14 Day Free Trial
            </h3>
            <p className="text-2xl font-bold text-primary mt-1">
              $39/month after trial
            </p>
          </div>

          <div className="space-y-3 mb-6">
            {SUBSCRIPTION_PLANS.SOLO.features.map((feature, index) => (
              <div key={index} className="flex items-center">
                <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>

          <Elements stripe={stripePromise}>
            <SubscriptionForm profile={profile} onSuccess={handleSuccess} />
          </Elements>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-500">
            Questions? Contact us at support@quotetier.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionOnboarding;
